class ColorSwatchesViewMore {
  static FIELDSET_SELECTOR = '.product-form__input--color';
  static WRAPPER_SELECTOR = '.product-form__color-swatches--view-more';
  static BUTTON_SELECTOR = '.product-form__color-swatches--view-more-button';
  static HIDDEN_CLASS = 'color-swatch-hidden';

  constructor() {
    this.expanded = new WeakSet();
    this.initialized = new WeakSet();
    this.lastWidth = new WeakMap();
    this.resizeObserver = new ResizeObserver((entries) => this.onResize(entries));

    document.addEventListener('click', (event) => {
      const button = event.target.closest(ColorSwatchesViewMore.BUTTON_SELECTOR);
      if (!button) return;
      const fieldset = button.closest(ColorSwatchesViewMore.FIELDSET_SELECTOR);
      if (!fieldset) return;
      this.init(fieldset);
      this.expanded.has(fieldset) ? this.collapse(fieldset) : this.expand(fieldset);
    });

    this.initAll(document);

    // Quick-add modals and theme editor section reloads inject product forms after page load.
    new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) this.initAll(node);
        });
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  initAll(root) {
    if (root.matches?.(ColorSwatchesViewMore.FIELDSET_SELECTOR)) this.init(root);
    root.querySelectorAll?.(ColorSwatchesViewMore.FIELDSET_SELECTOR).forEach((fieldset) => this.init(fieldset));
  }

  init(fieldset) {
    if (this.initialized.has(fieldset) || !fieldset.querySelector(ColorSwatchesViewMore.WRAPPER_SELECTOR)) return;
    this.initialized.add(fieldset);
    this.collapse(fieldset);
    this.resizeObserver.observe(fieldset);
  }

  // Pants type swatches share the item class but are nested deeper, so only direct children count.
  getSwatches(fieldset) {
    return Array.from(fieldset.children).filter((el) => el.matches('.product__color-swatch_item'));
  }

  collapse(fieldset) {
    // Defined inline in custom-colors-swatches.liquid. Hidden fieldsets (closed modal) cannot be measured;
    // it returns false and the server-rendered state stays until the fieldset is visible.
    if (!window.collapseColorSwatches?.(fieldset)) return;
    this.expanded.delete(fieldset);
  }

  expand(fieldset) {
    this.getSwatches(fieldset).forEach((swatch) => swatch.classList.remove(ColorSwatchesViewMore.HIDDEN_CLASS));
    this.expanded.add(fieldset);
    this.updateButton(fieldset, true, true);
  }

  updateButton(fieldset, visible, expanded) {
    const wrapper = fieldset.querySelector(ColorSwatchesViewMore.WRAPPER_SELECTOR);
    const button = wrapper?.querySelector(ColorSwatchesViewMore.BUTTON_SELECTOR);
    if (!wrapper || !button) return;

    wrapper.hidden = !visible;
    button.setAttribute('aria-expanded', String(expanded));

    const viewMoreText = button.querySelector('.view-more-text');
    const viewLessText = button.querySelector('.view-less-text');
    if (viewMoreText) viewMoreText.style.display = expanded ? 'none' : 'inline';
    if (viewLessText) viewLessText.style.display = expanded ? 'inline' : 'none';
  }

  onResize(entries) {
    // Deferred to the next frame: collapsing changes the fieldset height inside the observer callback otherwise.
    requestAnimationFrame(() => {
      entries.forEach(({ target }) => {
        if (!target.isConnected) {
          this.resizeObserver.unobserve(target);
          return;
        }
        const width = target.clientWidth;
        if (this.lastWidth.get(target) === width) return;
        this.lastWidth.set(target, width);
        if (!this.expanded.has(target)) this.collapse(target);
      });
    });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new ColorSwatchesViewMore();
});
